import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    Box,
    Container,
    Heading,
    SimpleGrid,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {
    useShopperCustomersMutation,
    useAuthHelper,
    AuthHelpers,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {pxToRem} from '../../../../utills/pxToRem'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../../../components/toggle-card'
import ProfileFields from './profile-fields'
import FormActionButtons from './form-action-button'
import {fonts} from '../../../../theme/foundations/fonts'
import UpdatePasswordFields from './update-password-fields'
import MyStoreCard from './my-store'
import CommunicationPreferencesCard from './communication-preferences'
import {formatDateToDMY, formatPhoneDisplay} from '../../../../utills/url'
import {isProfileComplete} from '../../../../utills/helperServices'
import {pushAnalyticsEvent} from '../../../../utills/analytics'

// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})

Skeleton.displayName = 'Skeleton'

const ProfileCard = ({allowPasswordChange = false, setPersonalInfoUpdated}) => {
    const {formatMessage} = useIntl()
    const headingRef = useRef(null)
    const {data: customer, refetch} = useCurrentCustomer()
    const {isRegistered, customerId} = customer

    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')

    const toast = useToast()
    const [isEditing, setIsEditing] = useState(false)
    const isValidProfile = useMemo(() => isProfileComplete(customer), [customer])

    const form = useForm({
        defaultValues: {
            firstName: customer?.firstName || '',
            lastName: customer?.lastName || '',
            email: customer?.email || '',
            phone: customer?.phoneMobile || '',
            birthday: customer?.birthday || ''
        }
    })

    useEffect(() => {
        if (!isValidProfile) {
            setIsEditing(true)
        }
    }, [isValidProfile])

    useEffect(() => {
        if (!isValidProfile && isEditing) {
            form.trigger()
        }
    }, [isEditing, isValidProfile])

    useEffect(() => {
        form.reset(
            {
                firstName: customer?.firstName || '',
                lastName: customer?.lastName || '',
                email: customer?.email || '',
                phone: customer?.phoneMobile || '',
                birthday: customer?.birthday || ''
            },
            {
                shouldValidate: true
            }
        )
    }, [
        customer?.firstName,
        customer?.lastName,
        customer?.email,
        customer?.phoneMobile,
        customer?.birthday
    ])

    const submit = async (values) => {
        try {
            form.clearErrors()
            updateCustomerMutation.mutate(
                {
                    parameters: {customerId},
                    body: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        phoneMobile: values.phone?.replace(/\D/g, ''),
                        email: values.email,
                        login: values.email,
                        ...(customer?.birthday && values.birthday === '' && {birthday: null}),
                        ...(values.birthday && {birthday: values.birthday})
                    }
                },
                {
                    onSuccess: async () => {
                        setIsEditing(false)
                        toast({
                            title: formatMessage({
                                defaultMessage: 'Personal Information updated',
                                id: 'profile_card.info.personal_information_updated'
                            }),
                            status: 'success',
                            isClosable: true,
                            position: 'top-right'
                        })
                        headingRef?.current?.focus()
                        await refetch()
                        setPersonalInfoUpdated(Date.now())
                        if (typeof window !== 'undefined' && window.dataLayer) {
                            await pushAnalyticsEvent({
                                eventName: 'accountUpdate',
                                userId: customerId,
                                email: values.email,
                                registered: isRegistered,
                                phone: values.phone?.replace(/\D/g, '') || '',
                                extraData: {
                                    first_name: values.firstName,
                                    last_name: values.lastName,
                                    birthday: values.birthday || null
                                }
                            })
                        }
                    }
                }
            )
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
        }
    }

    return (
        <ToggleCard
            id="my-profile"
            title={
                <Skeleton ref={headingRef} tabIndex="-1" height="30px" width="120px">
                    <FormattedMessage
                        defaultMessage="Personal Information"
                        id="profile_card.title.personal_information"
                    />
                </Skeleton>
            }
            editing={isEditing}
            isLoading={form.formState.isSubmitting}
            onEdit={isRegistered ? () => setIsEditing(true) : undefined}
            layerStyle="cardBordered"
            displayPreIcons={false}
            marginTop="0 !important"
            titleStyles={{
                color: 'black',
                fontSize: {base: pxToRem(16), lg: pxToRem(18)},
                letterSpacing: {base: pxToRem(2), lg: pxToRem(3)},
                fontWeight: 500,
                lineHeight: 'normal',
                marginBottom: pxToRem(10)
            }}
            paddingLeft={{base: pxToRem(15), lg: pxToRem(18)}}
            paddingRight={{base: pxToRem(15), lg: pxToRem(18)}}
            marginLeft={{base: pxToRem(10), lg: 0}}
            marginRight={{base: pxToRem(10), lg: 0}}
        >
            <ToggleCardEdit>
                <Container variant="form">
                    <form onSubmit={form.handleSubmit(submit)}>
                        <Stack>
                            {form.formState.errors?.global && (
                                <Alert status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text
                                        fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                        letterSpacing={pxToRem(1)}
                                        lineHeight={'normal'}
                                        ml={3}
                                    >
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}
                            <ProfileFields form={form} />
                            <FormActionButtons
                                saveButtonProps={{
                                    width: '100%',
                                    minWidth: {base: '100%', lg: '100%'}
                                }}
                                cancelButtonProps={{
                                    isDisabled: !isValidProfile
                                }}
                                onCancel={() => {
                                    setIsEditing(false)
                                    headingRef?.current?.focus()
                                    form.reset()
                                }}
                            />
                        </Stack>
                    </form>
                </Container>
            </ToggleCardEdit>
            <ToggleCardSummary>
                <SimpleGrid columns={{base: 1, lg: 2}} gap={{base: pxToRem(25), lg: pxToRem(20)}}>
                    <Box>
                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={500}
                                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                                color={'black'}
                            >
                                <FormattedMessage
                                    defaultMessage="Full Name"
                                    id="profile_card.label.full_name"
                                />
                            </Text>
                        </Skeleton>

                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={400}
                                color={'black'}
                            >
                                {customer?.firstName} {customer?.lastName}
                            </Text>
                        </Skeleton>
                    </Box>
                    <Box>
                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={500}
                                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                                color={'black'}
                            >
                                <FormattedMessage
                                    defaultMessage="Email"
                                    id="profile_card.label.email"
                                />
                            </Text>
                        </Skeleton>

                        <Skeleton height="21px" width="64px">
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={400}
                                color={'black'}
                            >
                                {customer?.email}
                            </Text>
                        </Skeleton>
                    </Box>
                    <Box>
                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={500}
                                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                                color={'black'}
                            >
                                <FormattedMessage
                                    defaultMessage="Phone Number"
                                    id="profile_card.label.phone"
                                />
                            </Text>
                        </Skeleton>

                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={400}
                                color={'black'}
                            >
                                {formatPhoneDisplay(customer?.phoneMobile) || (
                                    <FormattedMessage
                                        defaultMessage="Not provided"
                                        id="profile_card.message.not_provided"
                                    />
                                )}
                            </Text>
                        </Skeleton>
                    </Box>
                    <Box>
                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={500}
                                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                                color={'black'}
                            >
                                <FormattedMessage
                                    defaultMessage="Birthday"
                                    id="profile_card.label.birthday"
                                />
                            </Text>
                        </Skeleton>

                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={400}
                                color={'black'}
                            >
                                {(customer?.birthday && formatDateToDMY(customer.birthday)) || (
                                    <FormattedMessage
                                        defaultMessage="Not provided"
                                        id="profile_card.message.not_provided"
                                    />
                                )}
                            </Text>
                        </Skeleton>
                    </Box>
                </SimpleGrid>
            </ToggleCardSummary>
        </ToggleCard>
    )
}

ProfileCard.propTypes = {
    allowPasswordChange: PropTypes.bool,
    setPersonalInfoUpdated: PropTypes.bool
}

const PasswordCard = () => {
    const {formatMessage} = useIntl()
    const headingRef = useRef(null)
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    // Here we use AuthHelpers.UpdateCustomerPassword rather than invoking the updateCustomerPassword mutation directly
    // because the AuthHelper will re-authenticate the user's current session after the password change.
    const updateCustomerPassword = useAuthHelper(AuthHelpers.UpdateCustomerPassword)
    const toast = useToast()
    const [isEditing, setIsEditing] = useState(false)

    const form = useForm({
        defaultValues: {
            currentPassword: '',
            password: '',
            confirmPassword: ''
        }
    })

    const submit = async (values) => {
        if (values.confirmPassword !== values.password) {
            form.setError('root.global', {
                type: 'manual',
                message: 'Password and confirm password do not match.'
            })
            return
        }
        try {
            form.clearErrors()
            await updateCustomerPassword.mutateAsync({
                customer,
                password: values.password,
                currentPassword: values.currentPassword,
                shouldReloginCurrentSession: true
            })
            setIsEditing(false)
            toast({
                title: formatMessage({
                    defaultMessage: 'Password updated',
                    id: 'password_card.info.password_updated'
                }),
                status: 'success',
                isClosable: true,
                position: 'top-right'
            })
            headingRef?.current?.focus()
            form.reset()
        } catch (error) {
            const resObj = await error.response.json()
            form.setError('root.global', {type: 'manual', message: resObj.detail})
        }
    }

    return (
        <ToggleCard
            id="password"
            title={
                <Skeleton ref={headingRef} tabIndex="-1">
                    <FormattedMessage defaultMessage="Password" id="password_card.title.password" />
                </Skeleton>
            }
            editing={isEditing}
            isLoading={form.formState.isSubmitting}
            onEdit={isRegistered ? () => setIsEditing(true) : undefined}
            layerStyle="cardBordered"
            displayPreIcons={false}
            marginTop="0 important"
            titleStyles={{
                color: 'black',
                fontSize: {base: pxToRem(16), lg: pxToRem(18)},
                letterSpacing: {base: pxToRem(2), lg: pxToRem(3)},
                fontWeight: 500,
                lineHeight: 'normal',
                marginBottom: pxToRem(10)
            }}
            paddingLeft={{base: pxToRem(15), lg: pxToRem(18)}}
            paddingRight={{base: pxToRem(15), lg: pxToRem(18)}}
            marginLeft={{base: pxToRem(10), lg: 0}}
            marginRight={{base: pxToRem(10), lg: 0}}
        >
            <ToggleCardEdit>
                <Container variant="form">
                    <form onSubmit={form.handleSubmit(submit)}>
                        <Stack spacing={6}>
                            {form.formState.errors?.root?.global && (
                                <Alert data-testid="password-update-error" status="error">
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Box fontSize="sm" ml={3}>
                                        {form.formState.errors.root.global.message}
                                    </Box>
                                </Alert>
                            )}
                            <UpdatePasswordFields form={form} />
                            <FormActionButtons
                                saveButtonProps={{
                                    width: '100%',
                                    minWidth: {base: '100%', lg: '100%'}
                                }}
                                onCancel={() => {
                                    setIsEditing(false)
                                    headingRef?.current?.focus()
                                    form.reset()
                                }}
                            />
                        </Stack>
                    </form>
                </Container>
            </ToggleCardEdit>
            <ToggleCardSummary>
                <SimpleGrid columns={{base: 1, lg: 3}} spacing={4}>
                    <Box>
                        <Skeleton height="21px" width="84px">
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={500}
                                color={'black'}
                                marginBottom={{base: pxToRem(5), lg: pxToRem(10)}}
                            >
                                <FormattedMessage
                                    defaultMessage="Password"
                                    id="password_card.label.password"
                                />
                            </Text>
                        </Skeleton>

                        <Skeleton>
                            <Text
                                fontSize={{base: pxToRem(12), lg: pxToRem(13)}}
                                letterSpacing={pxToRem(1)}
                                lineHeight={'normal'}
                                fontStyle={'normal'}
                                fontWeight={400}
                                color={'black'}
                            >
                                &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;
                            </Text>
                        </Skeleton>
                    </Box>
                </SimpleGrid>
            </ToggleCardSummary>
        </ToggleCard>
    )
}

const AccountDetail = () => {
    const headingRef = useRef()
    const [personalInfoUpdated, setPersonalInfoUpdated] = useState(false)
    useEffect(() => {
        // Focus the 'Account Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const {isExternal} = useCustomerType()

    return (
        <Stack
            data-testid="account-detail-page"
            spacing={6}
            paddingRight={{lg: pxToRem(45)}}
            paddingBottom={{base: pxToRem(30), lg: pxToRem(40)}}
        >
            <Heading
                as="h2"
                fontSize={{base: pxToRem(18), lg: pxToRem(22)}}
                letterSpacing={pxToRem(3)}
                lineHeight={'normal'}
                fontWeight={600}
                color="black"
                ref={headingRef}
                fontFamily={fonts.primary}
                paddingLeft={{base: pxToRem(15), lg: 0}}
                paddingTop={{base: pxToRem(15), lg: pxToRem(25)}}
                marginBottom={{base: pxToRem(15), lg: pxToRem(25)}}
            >
                <FormattedMessage defaultMessage="Profile" id="account_detail.title.profile" />
            </Heading>
            <SimpleGrid
                columns={[1, 1, 1, 2]}
                gap={{base: pxToRem(15), lg: pxToRem(25)}}
                margin="0 !important"
            >
                <ProfileCard
                    allowPasswordChange={!isExternal}
                    setPersonalInfoUpdated={setPersonalInfoUpdated}
                />
                {!isExternal && <PasswordCard />}
                <CommunicationPreferencesCard personalInfoUpdated={personalInfoUpdated} />
                <MyStoreCard />
            </SimpleGrid>
        </Stack>
    )
}

AccountDetail.getTemplateName = () => 'account-detail'

export default AccountDetail
